/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useState, useCallback, useEffect } from 'preact/hooks';
import { events } from '@adobe-commerce/event-bus';
import { CustomerCompanyContext, updateCustomerGroup } from '@/company-switcher/api/customerCompanyContext';
import type { CompanyOption, CustomerCompanyInfo, UseCompanyDataProps, UseCompanyDataReturn } from '@/company-switcher/types/company';
import { config } from '@/company-switcher/api/initialize';
import { getGroupHeaderManager } from '@/company-switcher/api/setGroupHeaders';
import { getCompanyHeaderManager } from '@/company-switcher/api/setCompanyHeaders';

/**
 * Custom hook that manages company data fetching and state management
 * @returns {UseCompanyDataReturn} Company data and handlers
 */
export const useCompanyData = ({ onCompanyChange, pageSize = 100 }: UseCompanyDataProps = {}): UseCompanyDataReturn => {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [currentCompany, setCurrentCompany] = useState<CompanyOption>({ text: '', value: '' });


  /**
   * Loads company information from the API and updates state
   */
  const loadCompanyData = useCallback(async (): Promise<void> => {
    try {
      const customerCompanyContext = CustomerCompanyContext.getInstance();
      const companyInfo: CustomerCompanyInfo = await customerCompanyContext.getCustomerCompanyInfo(pageSize);
      setCompanies(companyInfo.customerCompanies);
      
      if (companyInfo.currentCompany?.name && companyInfo.currentCompany?.id) {
        setCurrentCompany({
          text: companyInfo.currentCompany.name, 
          value: companyInfo.currentCompany.id
        });
      }
    } catch (err) {
      console.error('Failed to load company data:', err);
    }
  }, [pageSize]);

  /**
   * Handles company selection change
   */
  const handleCompanyChange = useCallback(async (event: Event): Promise<void> => {
    const target = event.target as HTMLSelectElement;
    const companyId = target.value;
    
    // Update local state
    const selectedCompany = companies.find(c => c.value === companyId);
    if (selectedCompany) {
      setCurrentCompany(selectedCompany);
    }
    
    getCompanyHeaderManager().setCompanyHeaders(companyId);
    if (companyId) {
      sessionStorage.setItem(config.getConfig().companySessionStorageKey, companyId);
    } else {
      sessionStorage.removeItem(config.getConfig().companySessionStorageKey);
    }
    const groupId = await updateCustomerGroup();
    getGroupHeaderManager().setGroupHeaders(groupId);
    if (groupId) {
      sessionStorage.setItem(config.getConfig().groupSessionStorageKey, groupId);
    } else {
      sessionStorage.removeItem(config.getConfig().groupSessionStorageKey);
    }

    onCompanyChange?.({ id: companyId, name: selectedCompany?.text || '' });

    events.emit('companyContext/changed', companyId);
  }, [companies, onCompanyChange]);

  /**
   * Clears company context
   */
  const clearCompanyContext = useCallback((): void => {
    const customerCompanyContext = CustomerCompanyContext.getInstance();
    customerCompanyContext.resetCache();
    setCompanies([]);
    setCurrentCompany({ text: '', value: '' });
  }, []);

  /**
   * Handles authentication state changes
   */
  const handleAuthenticationChange = useCallback((authenticated: boolean) => {
    if (authenticated) {
      loadCompanyData();
    } else {
      clearCompanyContext();
    }
  }, [loadCompanyData, clearCompanyContext]);

  /**
   * Handles company context restoration
   */
  const handleCompanyContextRestored = useCallback((companyId: string | null) => {
    if (!companyId) {
      clearCompanyContext();
      return;
    }
    // Store the company ID to be restored when companies are loaded
    setCompanies(prevCompanies => {
      const restoredCompany = prevCompanies.find(company => company.value === companyId);
      if (restoredCompany) {
        setCurrentCompany(restoredCompany);
      }
      return prevCompanies;
    });
  }, [clearCompanyContext, setCompanies, setCurrentCompany]);

  /**
   * Handles company updated event
   */
  const handleCompanyUpdated = useCallback((eventData: { company: { id: string; name: string; } }) => {
    const { company } = eventData;

    setCompanies(prevCompanies => {
      const companyIndex = prevCompanies.findIndex(c => c.value === company.id);
      if (companyIndex === -1) {
        return prevCompanies;
      }
      return prevCompanies.map((c, i) =>
        i === companyIndex ? { ...c, text: company.name } : c
      );
    });
    
    setCurrentCompany({
      text: company.name,
      value: company.id
    });
  }, []);

  // Set up event listeners
  useEffect(() => {
    const unsubscribeAuth = events.on('authenticated', handleAuthenticationChange, { eager: true });
    const unsubscribeRestore = events.on('companyContext/changed', handleCompanyContextRestored, { eager: true });
    const unsubscribeCompanyUpdated = events.on('company/updated', handleCompanyUpdated);
    
    return () => {
      unsubscribeAuth?.off?.();
      unsubscribeRestore?.off?.();
      unsubscribeCompanyUpdated?.off?.();
    };
  }, [handleAuthenticationChange, handleCompanyContextRestored, handleCompanyUpdated]);

  return {
    companies,
    currentCompany,
    handleCompanyChange
  };
};
